import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import AuthCallback from "@/pages/AuthCallback";
import { Toaster } from "@/components/ui/sonner";
import { Hummingbird } from "@/components/Hummingbird";
import { api } from "@/lib/api";

function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();
    const [slow, setSlow] = React.useState(false);
    React.useEffect(() => {
        if (!loading) return;
        const t = setTimeout(() => setSlow(true), 6000);
        return () => clearTimeout(t);
    }, [loading]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F9F8F3] px-6">
                <div className="text-center max-w-sm">
                    <div className="hummingbird-float inline-block"><Hummingbird size={56} /></div>
                    <p className="mt-3 text-[#57675F]">Loading…</p>
                    {slow && (
                        <div className="mt-6 text-sm text-[#57675F]">
                            Taking longer than usual.
                            <div className="mt-3 flex flex-col gap-2 items-center">
                                <button
                                    onClick={() => window.location.reload()}
                                    className="rounded-full px-4 py-2 text-sm bg-[#0F5C60] hover:bg-[#0A464A] text-white"
                                    data-testid="slow-retry"
                                >
                                    Retry
                                </button>
                                <button
                                    onClick={async () => {
                                        try { await api.post("/auth/logout"); } catch (e) { /* noop */ }
                                        window.location.href = "/login";
                                    }}
                                    className="text-xs text-[#C0324F] underline"
                                    data-testid="slow-signout"
                                >
                                    Sign out and try again
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    return children;
}

function RouterShell() {
    const location = useLocation();
    // CRITICAL: handle session_id in hash BEFORE protected routing
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <RouterShell />
                    <Toaster position="top-right" richColors />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
